import { Component, effect, linkedSignal, model, untracked, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { S25RadioModule } from "../s25-radio/s25.radio.module";
import { S25SimpleCollapseModule } from "../s25-simple-collapse/s25.simple.collapse.module";
import { AdvancedStep } from "./s25ql.search.advanced.criteria.component";
import { SearchService } from "../../services/search/search.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { form, FormField, hidden, validate } from "@angular/forms/signals";
import { S25Util } from "../../util/s25-util";

@Component({
    selector: "s25-ng-ql-search-advanced-task",
    template: `
        <div>
            <div class="instructions">
                <p>
                    At least one task state and one task type are required, but you may choose any combination of task
                    types or states, or all of them. By default, task searches return only tasks due today.
                </p>
            </div>

            <s25-simple-collapse [header]="'Task States'" [class.invalid]="!form.states().valid()">
                <div class="collapseContainer">
                    <s25-ng-checkbox [formField]="form.states.outstanding">Outstanding</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.states.completed">Completed</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.states.denied">Denied</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.states.cancelled">Cancelled</s25-ng-checkbox>
                    <hr />
                    <s25-ng-checkbox [formField]="form.states.unread">Flagged Tasks Only</s25-ng-checkbox>
                    @for (error of form.states().errors(); track error) {
                        <p class="error-message">{{ error.message }}</p>
                    }
                </div>
            </s25-simple-collapse>

            <s25-simple-collapse [header]="'Task Types'" [class.invalid]="!form.types().valid()">
                <div class="collapseContainer">
                    <s25-ng-checkbox [formField]="form.types.todo">To Do Tasks</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.types.vcal">vCalendar To Do Tasks</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.types.authorization">Authorization Tasks</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.types.fyi">Notification FYIs</s25-ng-checkbox>
                    <s25-ng-checkbox [formField]="form.types.assignment">Assignment Tasks</s25-ng-checkbox>
                    @for (error of form.types().errors(); track error) {
                        <p class="error-message">{{ error.message }}</p>
                    }
                </div>
            </s25-simple-collapse>

            <s25-simple-collapse [header]="'Dates'" [class.invalid]="!form.dates().valid()">
                <div class="collapseContainer">
                    <div class="">
                        <label for="taskFromDate" class="ngBold">From</label>
                        <div>
                            <div class="flexRow">
                                <s25-ng-radio
                                    [formField]="form.dates.from_dt_type"
                                    [radioValue]="'number'"
                                    [style.display]="'block'"
                                >
                                    Today +/-
                                </s25-ng-radio>
                                @if (!form.dates.from_dt_num().hidden()) {
                                    <input
                                        [formField]="form.dates.from_dt_num"
                                        type="number"
                                        class="c-input"
                                        [id]="'taskFromDate'"
                                    />
                                }
                            </div>
                            <div class="flexRow">
                                <s25-ng-radio [formField]="form.dates.from_dt_type" [radioValue]="'date'">
                                    Specific Date
                                </s25-ng-radio>
                                @if (!form.dates.from_dt_date().hidden()) {
                                    <s25-ng-editable-date
                                        [formField]="form.dates.from_dt_date"
                                        [inputId]="'taskFromDate'"
                                    />
                                }
                            </div>
                        </div>
                    </div>

                    <div>
                        <label for="taskUntilDate" class="ngBold">Until</label>
                        <div class="flexRow">
                            <s25-ng-radio
                                [formField]="form.dates.until_dt_type"
                                [radioValue]="'number'"
                                [style.display]="'block'"
                                >Day(s) Following</s25-ng-radio
                            >
                            @if (!form.dates.until_dt_num().hidden()) {
                                <input
                                    [formField]="form.dates.until_dt_num"
                                    type="number"
                                    class="c-input"
                                    [id]="'taskUntilDate'"
                                />
                            }
                        </div>
                        <div class="flexRow">
                            <s25-ng-radio [formField]="form.dates.until_dt_type" [radioValue]="'date'">
                                Specific Date
                            </s25-ng-radio>
                            @if (!form.dates.until_dt_date().hidden()) {
                                <s25-ng-editable-date
                                    [formField]="form.dates.until_dt_date"
                                    [inputId]="'taskUntilDate'"
                                />
                            }
                        </div>
                    </div>
                </div>
            </s25-simple-collapse>

            <s25-simple-collapse [header]="'Assigned To'" [class.invalid]="!form.assignedTo().valid()">
                <div class="collapseContainer">
                    <s25-ng-multiselect-search-criteria
                        [type]="'contacts'"
                        [formField]="form.assignedTo"
                        [popoverPlacement]="'right'"
                        [showResults]="true"
                    />
                </div>
            </s25-simple-collapse>

            <s25-simple-collapse [header]="'Assigned From'" [class.invalid]="!form.assignedFrom().valid()">
                <div class="collapseContainer">
                    <s25-ng-multiselect-search-criteria
                        [type]="'contacts'"
                        [formField]="form.assignedFrom"
                        [popoverPlacement]="'right'"
                        [showResults]="true"
                    />
                </div>
            </s25-simple-collapse>
        </div>
    `,
    styles: `
        .instructions {
            background-color: #f5f5f5;
            border: 1px solid #ccc;
            padding: 10px;
            margin-bottom: 10px;
            color: #666;
            font-size: 90%;
            line-height: 140%;
        }

        .collapseContainer {
            padding: 1em;
        }

        s25-ng-checkbox {
            display: block;
        }

        .flexRow {
            display: flex;
            gap: 1em;
            flex-wrap: wrap;
        }

        s25-simple-collapse.invalid {
            --simple-collapse--border-color: var(--error-message-color);
            --simple-collapse--header-bg-color: var(--color--surface-bg-error);
        }

        .error-message {
            color: var(--error-message-color);
            font-size: 0.9em;
            padding-block-start: 1rem;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        S25MultiselectModule,
        S25SimpleCollapseModule,
        S25EditableModule,
        S25RadioModule,
        FormsModule,
        S25CheckboxModule,
        FormField,
    ],
})
export class S25qlSearchAdvancedInputTaskComponent {
    readonly task = model.required<AdvancedStep>();

    readonly formModel = linkedSignal(() => {
        const task = this.task();
        const fromDt = task?.dateBean?.[0].step_param[0].from_dt ?? 0;
        const untilDt = task?.dateBean?.[0].step_param[0].until_dt ?? 1;
        return {
            states: {
                outstanding: task?.states?.outstanding ?? !task,
                completed: task?.states?.completed ?? false,
                denied: task?.states?.denied ?? false,
                cancelled: task?.states?.cancelled ?? false,
                unread: task?.states?.unread ?? false,
            },
            types: {
                todo: task?.types?.todo ?? !task,
                vcal: task?.types?.vcal ?? !task,
                authorization: task?.types?.authorization ?? !task,
                fyi: task?.types?.fyi ?? !task,
                assignment: task?.types?.assignment ?? !task,
            },
            dates: SearchService.fromUntilHelper(fromDt, untilDt),
            assignedTo: this.toDropdownItems(task?.contactBeans![0].step_param ?? []),
            assignedFrom: this.toDropdownItems(task?.contactBeans![1].step_param ?? []),
        };
    });
    readonly form = form(this.formModel, (schemaPath) => {
        hidden(schemaPath.dates.from_dt_num, () => this.formModel().dates.from_dt_type !== "number");
        hidden(schemaPath.dates.from_dt_date, () => this.formModel().dates.from_dt_type !== "date");
        hidden(schemaPath.dates.until_dt_num, () => this.formModel().dates.until_dt_type !== "number");
        hidden(schemaPath.dates.until_dt_date, () => this.formModel().dates.until_dt_type !== "date");
        validate(schemaPath.states, () => {
            const hasState = S25Util.entries(this.formModel().states).some(([state, val]) => val && state !== "unread");
            if (hasState) return undefined;
            return { kind: "error", message: "At least one state must be selected" };
        });
        validate(schemaPath.types, () => {
            const hasType = Object.values(this.formModel().types).some((val) => val);
            if (hasType) return undefined;
            return { kind: "error", message: "At least one type must be selected" };
        });
    });

    constructor() {
        effect(() => {
            // Update the task on form changes
            if (!this.form().dirty()) return;

            const formModel = untracked(this.formModel);
            const dates = formModel.dates;
            const from = dates.from_dt_type === "date" ? dates.from_dt_date : dates.from_dt_num;
            const until = dates.until_dt_type === "date" ? dates.until_dt_date : dates.until_dt_num;
            this.task.update((task) => ({
                ...task,
                states: formModel.states,
                types: formModel.types,
                dateBean: [{ step_param: [{ ...formModel.dates, from_dt: from, until_dt: until }] }],
                contactBeans: [
                    { step_type_id: 210, step_param: this.toDropdownItems(formModel.assignedTo), title: "Assigned To" },
                    {
                        step_type_id: 210,
                        step_param: this.toDropdownItems(formModel.assignedFrom),
                        title: "Assigned From",
                    },
                ],
            }));

            this.form().reset();
        });
    }

    toDropdownItems(items: { itemId?: string | number; itemName?: string }[]): DropDownItem[] {
        return items.filter(({ itemId }) => itemId).map(({ itemId, itemName }) => ({ itemId, itemName }));
    }

    validate() {
        return this.form().valid();
    }
}
